import { useEffect, useState } from "react";
import { altioraToast } from "@/components/altiora/toast";
import { api } from "@/lib/api";
import { Loader2, Save, RotateCcw, ExternalLink, Search, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";

/**
 * AdminInstitutionsEditor · Back-office to override catalog thresholds without
 * a redeployment. Each row shows the merged (static + override) view; edits are
 * saved as a JSON *patch* that Mongo stores in `institutions_overrides`.
 */
export default function AdminInstitutionsEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institutions/admin/effective-catalog");
      setItems(data.items || []);
    } catch (err) {
      altioraToast.error(err?.response?.data?.detail || "Chargement impossible.");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = items.filter((i) =>
    !q || `${i.name} ${i.short_name} ${i.focus_fr || ""}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]">
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-24">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
          Back-office · Éditeur catalogue institutions
        </div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl tracking-tight leading-tight">
          Ajuster les critères publics<br /><span className="text-[#0B2545]">sans redéploiement.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-[#5B5B62] leading-relaxed">
          Chaque valeur laissée vide utilise le défaut du catalogue statique.
          Les modifications s'appliquent immédiatement au comparateur pour tous les dossiers.
        </p>

        <div className="mt-8 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B8B8F]" strokeWidth={2} />
            <input
              type="text"
              placeholder="Rechercher une institution…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-inst-search"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-[#E4E1D8] text-sm"
            />
          </div>
          <button
            onClick={refresh}
            data-testid="admin-inst-refresh"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] hover:border-[#0B2545] px-3 py-2 text-xs text-[#5B5B62] hover:text-[#0B2545]"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} /> Actualiser
          </button>
          <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
            {items.length} institutions
          </div>
        </div>

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-sm text-[#5B5B62]">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> Chargement…
          </div>
        )}

        <div className="mt-6 space-y-3" data-testid="admin-inst-list">
          {filtered.map((inst) => (
            <InstitutionEditorCard
              key={inst.id} inst={inst}
              expanded={expandedId === inst.id}
              onToggle={() => setExpandedId(expandedId === inst.id ? null : inst.id)}
              onSaved={refresh}
            />
          ))}
        </div>
      </div>
    </div>
  );
}


function InstitutionEditorCard({ inst, expanded, onToggle, onSaved }) {
  const [name, setName] = useState(inst.name || "");
  const [focus, setFocus] = useState(inst.focus_fr || "");
  const [verified, setVerified] = useState(inst.last_verified_at || "");
  const [products, setProducts] = useState(() => Object.fromEntries((inst.products || []).map((p) => [
    p.id, {
      min_credit_score: p.min_credit_score ?? "",
      min_income_cad:   p.min_income_cad ?? "",
      max_dti:          p.max_dti ?? "",
      min_years_in_operation: p.min_years_in_operation ?? "",
      min_annual_revenue_cad: p.min_annual_revenue_cad ?? "",
    }
  ])));
  const [saving, setSaving] = useState(false);

  const setProdField = (pid, field, val) => {
    setProducts((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: val } }));
  };

  const buildPatch = () => {
    const patch = {};
    if (name && name !== inst.name) patch.name = name;
    if (focus && focus !== inst.focus_fr) patch.focus_fr = focus;
    if (verified && verified !== inst.last_verified_at) patch.last_verified_at = verified;
    const prodPatches = {};
    for (const [pid, fields] of Object.entries(products)) {
      const orig = (inst.products || []).find((p) => p.id === pid) || {};
      const patchP = {};
      for (const [k, v] of Object.entries(fields)) {
        if (v === "" || v == null) continue;
        const num = Number(v);
        if (!Number.isFinite(num)) continue;
        if (num !== orig[k]) patchP[k] = num;
      }
      if (Object.keys(patchP).length > 0) prodPatches[pid] = patchP;
    }
    if (Object.keys(prodPatches).length > 0) patch.products = prodPatches;
    return patch;
  };

  const save = async () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      altioraToast.info("Aucune modification à enregistrer.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/institutions/admin/override/${inst.id}`, { patch });
      altioraToast.success(`${inst.short_name} — override enregistré.`);
      onSaved?.();
    } catch (err) {
      altioraToast.error(err?.response?.data?.detail || "Enregistrement impossible.");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!inst.has_override) return;
    setSaving(true);
    try {
      await api.delete(`/institutions/admin/override/${inst.id}`);
      altioraToast.success(`${inst.short_name} — override supprimé.`);
      onSaved?.();
    } catch (err) {
      altioraToast.error(err?.response?.data?.detail || "Suppression impossible.");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-[#E4E1D8] bg-white overflow-hidden"
         data-testid={`admin-inst-card-${inst.id}`}>
      <button type="button" onClick={onToggle}
              className="w-full flex items-center gap-3 px-4 py-3 group"
              data-testid={`admin-inst-toggle-${inst.id}`}>
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="font-display text-base text-[#1F2937] tracking-tight">{inst.name}</span>
            {inst.has_override && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-navy-50 text-[#7A5B14] bg-[#FBF6EA]"
                    data-testid={`admin-inst-badge-override-${inst.id}`}>
                <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2} /> Override actif
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-[#5B5B62]">
            {(inst.countries || []).join(" · ")} · {(inst.products || []).length} produits
          </div>
        </div>
        {expanded
          ? <ChevronUp className="h-4 w-4 text-[#5B5B62]" strokeWidth={2} />
          : <ChevronDown className="h-4 w-4 text-[#5B5B62]" strokeWidth={2} />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-[#E4E1D8] pt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Nom affiché" value={name} setValue={setName}
                    testid={`admin-inst-name-${inst.id}`} />
            <Field label="Focus (FR)" value={focus} setValue={setFocus}
                    testid={`admin-inst-focus-${inst.id}`} />
            <Field label="Dernière vérification (YYYY-MM-DD)"
                    value={verified} setValue={setVerified}
                    testid={`admin-inst-verified-${inst.id}`} />
          </div>

          <div className="space-y-3">
            {(inst.products || []).map((p) => (
              <div key={p.id} className="rounded-lg border border-[#E4E1D8] bg-[#FBF6EA]/30 p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#7A5B14]">
                  {p.id} · {p.label_fr}
                </div>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2">
                  <NumField label="Score crédit min" value={products[p.id]?.min_credit_score}
                    setValue={(v) => setProdField(p.id, "min_credit_score", v)}
                    testid={`admin-inst-${inst.id}-${p.id}-cs`} />
                  <NumField label="Revenu min ($)" value={products[p.id]?.min_income_cad}
                    setValue={(v) => setProdField(p.id, "min_income_cad", v)}
                    testid={`admin-inst-${inst.id}-${p.id}-income`} />
                  <NumField label="DTI max (0-1)" value={products[p.id]?.max_dti}
                    setValue={(v) => setProdField(p.id, "max_dti", v)} step="0.01"
                    testid={`admin-inst-${inst.id}-${p.id}-dti`} />
                  <NumField label="Années opé. min" value={products[p.id]?.min_years_in_operation}
                    setValue={(v) => setProdField(p.id, "min_years_in_operation", v)}
                    testid={`admin-inst-${inst.id}-${p.id}-ops`} />
                  <NumField label="CA annuel min ($)" value={products[p.id]?.min_annual_revenue_cad}
                    setValue={(v) => setProdField(p.id, "min_annual_revenue_cad", v)}
                    testid={`admin-inst-${inst.id}-${p.id}-rev`} />
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving}
                    data-testid={`admin-inst-save-${inst.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-4 py-2 text-sm">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                       : <Save className="h-3.5 w-3.5" strokeWidth={2} />}
              Enregistrer les changements
            </button>
            {inst.has_override && (
              <button onClick={reset} disabled={saving}
                      data-testid={`admin-inst-reset-${inst.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 px-3 py-2 text-xs">
                <RotateCcw className="h-3 w-3" strokeWidth={2} /> Revenir au défaut
              </button>
            )}
            {inst.override_updated_at && (
              <div className="ml-auto text-[10px] font-mono text-[#8B8B8F]">
                Dernier override : {new Date(inst.override_updated_at).toLocaleString("fr-CA")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function Field({ label, value, setValue, testid }) {
  return (
    <label className="block">
      <div className="mb-1 text-[9px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</div>
      <input type="text" value={value} onChange={(e) => setValue(e.target.value)}
             data-testid={testid}
             className="w-full rounded-md border border-[#E4E1D8] px-2.5 py-1.5 text-xs" />
    </label>
  );
}

function NumField({ label, value, setValue, testid, step }) {
  return (
    <label className="block">
      <div className="mb-1 text-[9px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</div>
      <input type="number" step={step || "1"} value={value ?? ""} onChange={(e) => setValue(e.target.value)}
             data-testid={testid}
             className="w-full rounded-md border border-[#E4E1D8] px-2.5 py-1.5 text-xs" />
    </label>
  );
}
